import { Input, Select } from '@amzn/awsui-components-react';
import { available_locale_options, getSelection } from '../../configs/multi-select-config';
import { Poll } from '../../data-types';
import React from 'react';

// Constants
const SURFACE_TYPES = {
    ASIN: 'ASIN',
    EVENT: 'EVENT',
} as const;

const SUFFIXES = {
    ASIN: '#ASIN',
    EVENT: '#EVENT',
} as const;

const CUSTOM_EVENTS = {
    CLEAR_FLASHBAR: 'clearFlashbar',
    UPDATE_SURFACE: 'updateSurface',
} as const;

const SURFACE_OPTIONS = [
    {
        label: 'Select Surface Type',
        id: '',
        disabled: true,
    },
    {
        label: 'X-Ray / Poll DP',
        id: SURFACE_TYPES.ASIN,
    },
    {
        label: 'Event DP',
        id: SURFACE_TYPES.EVENT,
    },
];

const RESPONSIVE_CLASSES = {
    LABEL: 'col-xxxs-12 col-xxs-4',
    FIELD: 'col-xxxs-12 col-xxs-8',
} as const;

// Types
export interface Props {
    poll: Poll;
    isEdit?: boolean;
    onChange: (e: CustomEvent) => void;
    onPollChanged?: (poll: Poll) => void;
}

// Utility functions
const getSurfaceTypeFromAssociation = (association: string): string => {
    if (association.includes(SUFFIXES.EVENT)) return SURFACE_TYPES.EVENT;
    if (association.includes(SUFFIXES.ASIN)) return SURFACE_TYPES.ASIN;
    return '';
};

const hasAnySuffix = (value: string): boolean => {
    return value.includes(SUFFIXES.ASIN) || value.includes(SUFFIXES.EVENT);
};

const removeSuffixes = (value: string): string => {
    return value.replace(new RegExp(SUFFIXES.ASIN, 'g'), '').replace(new RegExp(SUFFIXES.EVENT, 'g'), '').trim();
};

const getSuffixForSurfaceType = (surfaceType: string): string => {
    return surfaceType === SURFACE_TYPES.EVENT ? SUFFIXES.EVENT : SUFFIXES.ASIN;
};

class PollMetadata extends React.Component<Props> {
    // Event handlers
    private handleChange = (e: CustomEvent): void => {
        this.props.onChange(e);
    };

    private handleAssociationBlur = (e: CustomEvent): void => {
        const value = e.detail?.value;
        if (!value || hasAnySuffix(value)) return;

        const surfaceType = this.getSurfaceType();
        if (!surfaceType) return;

        const suffix = getSuffixForSurfaceType(surfaceType);
        const newEvent = this.createModifiedEvent(e, value + suffix);
        this.props.onChange(newEvent);
    };

    private handleSurfaceChange = (e: CustomEvent): void => {
        const surfaceType = e.detail?.selectedOption?.id;

        // Pass original event to parent
        this.props.onChange(e);

        if (surfaceType) {
            this.notifyParentOfSurfaceChange(surfaceType);
            this.updateAssociationWithSuffix(surfaceType);
        }
    };

    // Helper methods
    private getSurfaceType = (): string => {
        const association = this.props.poll.association || '';
        return getSurfaceTypeFromAssociation(association);
    };

    private createModifiedEvent(originalEvent: CustomEvent, newValue: string): CustomEvent {
        return {
            ...originalEvent,
            detail: {
                ...originalEvent.detail,
                value: newValue,
            },
        } as CustomEvent;
    }

    private notifyParentOfSurfaceChange(surfaceType: string): void {
        const events = [
            new CustomEvent(CUSTOM_EVENTS.UPDATE_SURFACE, {
                detail: { surfaceType },
            }),
            new CustomEvent(CUSTOM_EVENTS.CLEAR_FLASHBAR),
        ];

        events.forEach((event) => this.props.onChange(event));
    }

    private updateAssociationWithSuffix(surfaceType: string): void {
        if (!this.props.onPollChanged) return;

        const currentAssociation = this.props.poll.association || '';
        const cleanValue = removeSuffixes(currentAssociation);

        if (!cleanValue) return;

        const suffix = getSuffixForSurfaceType(surfaceType);
        const newAssociation = cleanValue + suffix;

        const updatedPoll = {
            ...this.props.poll,
            association: newAssociation,
        };

        this.props.onPollChanged(updatedPoll);
    }

    // Render helpers
    private renderFormField(label: string, id: string, component: JSX.Element): JSX.Element {
        return (
            <div className="awsui-row">
                <h4 className={RESPONSIVE_CLASSES.LABEL}>{label}</h4>
                {React.cloneElement(component, {
                    className: RESPONSIVE_CLASSES.FIELD,
                    id,
                    ...component.props,
                })}
            </div>
        );
    }

    private renderInput(
        value: string | undefined,
        disabled = false,
        placeholder?: string,
        onBlur?: (e: CustomEvent) => void,
    ): JSX.Element {
        return (
            <Input
                type="text"
                value={value || ''}
                onChange={this.handleChange}
                disabled={disabled}
                placeholder={placeholder}
                onBlur={onBlur}
            />
        );
    }

    private renderSelect(
        options: any[],
        selectedOption: any,
        disabled = false,
        onChange?: (e: CustomEvent) => void,
    ): JSX.Element {
        return (
            <Select
                options={options}
                selectedLabel="Selected"
                selectedOption={selectedOption}
                onChange={onChange || this.handleChange}
                disabled={disabled}
            />
        );
    }

    render(): JSX.Element {
        const { poll, isEdit } = this.props;

        return (
            <div className="awsui-util-container">
                <div className="awsui-util-container-header">Metadata</div>
                <div className="awsui-grid">
                    {this.renderFormField('Poll ID:', 'id', this.renderInput(poll.id, true))}

                    {this.renderFormField(
                        'Locale',
                        'locale',
                        this.renderSelect(
                            available_locale_options,
                            getSelection(available_locale_options, poll.locale),
                            isEdit,
                        ),
                    )}

                    {this.renderFormField(
                        'Association:',
                        'association',
                        this.renderInput(poll.association, false, 'Enter Artist ID', this.handleAssociationBlur),
                    )}

                    {this.renderFormField(
                        'Surface Type:',
                        'surfaceType',
                        this.renderSelect(
                            SURFACE_OPTIONS,
                            getSelection(SURFACE_OPTIONS, this.getSurfaceType()),
                            false,
                            this.handleSurfaceChange,
                        ),
                    )}

                    {this.renderFormField('Poll title:', 'title', this.renderInput(poll.title))}

                    {this.renderFormField('Creation time:', 'created_at', this.renderInput(poll.created_at, true))}

                    {this.renderFormField('Last updated:', 'last_updated', this.renderInput(poll.last_updated, true))}
                </div>
            </div>
        );
    }
}

export default PollMetadata;
